<template>
    <div class="tasks-page">
        <Header ref="header" helpURL="studio/tasks/">
            <template #page-slot>            
                Tasks
            </template>
        </Header>
        <div class="main-page-div">
            <main class="main-display-area">
                <!-- Filter Bar -->
                <div class="filter-bar">
                    <div class="filter-controls">
                        <div class="filter-item">
                            <label for="start-date">Start Date</label>
                            <input 
                                id="start-date"
                                v-model="startDate" 
                                type="date" 
                                class="date-input"
                            />
                        </div>
                        <div class="filter-item">
                            <label for="end-date">End Date</label>
                            <input 
                                id="end-date"
                                v-model="endDate" 
                                type="date" 
                                class="date-input"
                            />
                        </div>
                        <div class="filter-item">
                            <label for="status-filter">Status</label>
                            <div ref="statusDropdownRef" class="multi-select-wrapper">
                                <div class="multi-select-display" @click="toggleStatusDropdown">
                                    <span class="selected-text">
                                        {{ statusFilter.length === 0 ? 'All' : statusFilter.join(', ') }}
                                    </span>
                                    <span class="dropdown-arrow">▼</span>
                                </div>
                                <div v-if="showStatusDropdown" class="multi-select-dropdown">
                                    <label v-for="option in statusOptions" :key="option.value" class="checkbox-item">
                                        <input 
                                            type="checkbox" 
                                            :value="option.value" 
                                            v-model="tempStatusFilter"
                                        />
                                        <span>{{ option.label }}</span>
                                    </label>
                                    <div class="dropdown-actions">
                                        <button class="btn-clear" @click="clearStatusFilter">
                                            Clear
                                        </button>
                                        <button class="btn-apply" @click="applyStatusFilter">
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="filter-item">
                            <label for="type-filter">Type</label>
                            <div ref="typeDropdownRef" class="multi-select-wrapper">
                                <div class="multi-select-display" @click="toggleTypeDropdown">
                                    <span class="selected-text">
                                        {{ typeFilter.length === 0 ? 'All' : typeFilter.join(', ') }}
                                    </span>
                                    <span class="dropdown-arrow">▼</span>
                                </div>
                                <div v-if="showTypeDropdown" class="multi-select-dropdown">
                                    <label v-for="option in availableTypes" :key="option" class="checkbox-item">
                                        <input 
                                            type="checkbox" 
                                            :value="option" 
                                            v-model="tempTypeFilter"
                                        />
                                        <span>{{ option }}</span>
                                    </label>
                                    <div class="dropdown-actions">
                                        <button class="btn-clear" @click="clearTypeFilter">
                                            Clear
                                        </button>
                                        <button class="btn-apply" @click="applyTypeFilter">
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="help-tip-container">
                        <HelpTip> Only 20 tasks are shown at a time. Use the filters to find specific tasks.  
                        </HelpTip>
                    </div>
                </div>

                <div v-if="loading" class="empty-state">
                    <div class="loading">Loading tasks...</div>
                </div>
                <template v-else>
                    <div v-if="tasks && tasks.length > 0" class="tasks-container">
                        <!-- Active Tasks Section -->
                        <div class="task-section">
                            <div class="task-section-header" @click="toggleActiveSection">
                                <div class="section-title-container">
                                    <span class="collapse-icon">{{ showActiveTasks ? '▼' : '▶' }}</span>
                                    <h3 class="section-title">Active Tasks ({{ activeTasks.length }})</h3>
                                </div>
                            </div>
                            <div v-if="showActiveTasks" class="tasks-list scrollbar">
                                <TaskCard 
                                    v-for="(task, index) in activeTasks" 
                                    :key="task.id || task.name || index"
                                    :session="task"
                                    :permissions="{ trainer_write: true }"
                                    @delete-session="handleTaskDeleted"
                                />
                                <div v-if="activeTasks.length === 0" class="empty-message">
                                    No active tasks
                                </div>
                            </div>
                        </div>

                        <!-- Historical Tasks Section -->
                        <div class="task-section">
                            <div class="task-section-header" @click="toggleHistoricalSection">
                                <div class="section-title-container">
                                    <span class="collapse-icon">{{ showHistoricalTasks ? '▼' : '▶' }}</span>
                                    <h3 class="section-title">Historical Tasks ({{ historicalTasks.length }})</h3>
                                </div>
                            </div>
                            <div v-if="showHistoricalTasks" class="tasks-list scrollbar">
                                <TaskCard 
                                    v-for="(task, index) in historicalTasks" 
                                    :key="task.id || task.name || index"
                                    :session="task"
                                    :permissions="{ trainer_write: true }"
                                    @delete-session="handleTaskDeleted"
                                />
                                <div v-if="historicalTasks.length === 0" class="empty-message">
                                    No historical tasks
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="empty-state">
                        No tasks found
                    </div>
                </template>
            </main>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import Header from '@/components/Header.vue';
import TaskCard from '@/components/Apps/TaskCard.vue';
import HelpTip from '@/components/HelpTip.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';

const $toast = useToast();
const dataConnect = new DatastoreConnect();

const tasks = ref([]);
const loading = ref(false);
const error = ref(null);
const showActiveTasks = ref(true);
const showHistoricalTasks = ref(false);

// Filter states
const startDate = ref('');
const endDate = ref('');
const statusFilter = ref([]);
const tempStatusFilter = ref([]);
const showStatusDropdown = ref(false);
const statusDropdownRef = ref(null);
const typeFilter = ref([]);
const tempTypeFilter = ref([]);
const showTypeDropdown = ref(false);
const typeDropdownRef = ref(null);
let pollInterval = null;

const statusOptions = [
    { value: 'completed', label: 'Completed' },
    { value: 'error', label: 'Error' },
    { value: 'queued', label: 'Queued' },
    { value: 'other', label: 'Other' },
    { value: 'deleted', label: 'Deleted' }
];

// Get unique types from tasks
const availableTypes = computed(() => {
    const types = new Set();
    tasks.value.forEach(task => {
        if (task.type) {
            types.add(task.type);
        }
    });
    return Array.from(types).sort();
});

const toggleStatusDropdown = () => {
    if (!showStatusDropdown.value) {
        // When opening, copy current filter to temp
        tempStatusFilter.value = [...statusFilter.value];
    }
    showStatusDropdown.value = !showStatusDropdown.value;
};

const toggleTypeDropdown = () => {
    if (!showTypeDropdown.value) {
        // When opening, copy current filter to temp
        tempTypeFilter.value = [...typeFilter.value];
    }
    showTypeDropdown.value = !showTypeDropdown.value;
};

const applyStatusFilter = () => {
    statusFilter.value = [...tempStatusFilter.value];
    showStatusDropdown.value = false;
    loadTasks();
};

const applyTypeFilter = () => {
    typeFilter.value = [...tempTypeFilter.value];
    showTypeDropdown.value = false;
    loadTasks();
};

const clearStatusFilter = () => {
    tempStatusFilter.value = [];
    statusFilter.value = [];
    showStatusDropdown.value = false;
    loadTasks();
};

const clearTypeFilter = () => {
    tempTypeFilter.value = [];
    typeFilter.value = [];
    showTypeDropdown.value = false;
    loadTasks();
};

// Click outside handler to close dropdown
const handleClickOutside = (event) => {
    if (statusDropdownRef.value && !statusDropdownRef.value.contains(event.target)) {
        showStatusDropdown.value = false;
    }
    if (typeDropdownRef.value && !typeDropdownRef.value.contains(event.target)) {
        showTypeDropdown.value = false;
    }
};

// Add event listener on mount
onMounted(async () => {
    document.addEventListener('click', handleClickOutside);
    await loadTasks();
    
    // Start polling every 5 seconds
    pollInterval = setInterval(() => {
        loadTasks(true);
    }, 5000);
});

// Remove event listener on unmount
onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    
    // Clear polling interval
    if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
    }
});

const activeTasks = computed(() => {
    return tasks.value.filter(task => {
        const status = task.status?.toLowerCase() || '';
        return !status.includes('complete') && !status.includes('error') && !status.includes('terminated');
    });
});

const historicalTasks = computed(() => {
    return tasks.value.filter(task => {
        const status = task.status?.toLowerCase() || '';
        return status.includes('complete') || status.includes('error') || status.includes('terminated');
    });
});

const toggleActiveSection = () => {
    showActiveTasks.value = !showActiveTasks.value;
};

const toggleHistoricalSection = () => {
    showHistoricalTasks.value = !showHistoricalTasks.value;
};

// Watch for filter changes (excluding statusFilter which is applied manually)
watch([startDate, endDate], () => {
    loadTasks();
});

async function loadTasks(silent = false) {
    if (!silent) {
        loading.value = true;
    }
    error.value = null;
    try {
        // Build params object with only selected filters
        const params = {
            recent: true
        };
        
        if (startDate.value) {
            params.start_date = startDate.value;
        }
        
        if (endDate.value) {
            params.end_date = endDate.value;
        }
        
        if (statusFilter.value && statusFilter.value.length > 0) {
            params.status = statusFilter.value;
        }
        
        if (typeFilter.value && typeFilter.value.length > 0) {
            params.types = typeFilter.value;
        }
        
        const response = await dataConnect.listTasks(params);
        if (response && response.result) {
            tasks.value = response.result.tasks || [];
        } else {
            tasks.value = [];
        }
    } catch (err) {
        error.value = 'Failed to load tasks';
        $toast.error('Failed to load tasks');
        console.error('Error loading tasks:', err);
    } finally {
        if (!silent) {
            loading.value = false;
        }
    }
}

async function handleTaskDeleted(deletedTask) {
    $toast.success('Task deleted successfully');
    // Refresh the task list from backend
    await loadTasks();
}
</script>

<style lang="scss" scoped>
.tasks-page {
    width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;
}

.main-page-div {
    width: 100%;
    flex: 1;
    min-height: 0;
}

.main-display-area {
    position: relative;
}

.filter-bar {
    display: flex;
    align-items: center;
    padding: 4px 24px;
    background: var(--gray-100);
    border-bottom: 1px solid var(--gray-200);
    gap: 24px;
}

.filter-heading {
    font-size: 16px;
    font-weight: 600;
    color: var(--body-text-color);
    margin: 0;
    min-width: 60px;
}

.filter-controls {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
    flex-wrap: wrap;
}

.filter-item {
    display: flex;
    align-items: center;
    gap: 8px;

    label {
        font-size: 12px;
        font-weight: 500;
        color: var(--body-text-color-secondary);
        white-space: nowrap;
    }
}

.date-input {
    padding: 6px 10px;
    border: 1px solid var(--gray-200);
    border-radius: 4px;
    font-size: 14px;
    background: white;
    color: var(--body-text-color);
    min-width: 140px;

    &:focus {
        outline: none;
        border-color: var(--primary-color, #1976d2);
    }
}

.multi-select-wrapper {
    position: relative;
    min-width: 180px;
}

.multi-select-display {
    padding: 6px 10px;
    border: 1px solid var(--gray-200);
    border-radius: 4px;
    font-size: 14px;
    background: white;
    color: var(--body-text-color);
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 32px;

    &:hover {
        border-color: var(--gray-300);
    }

    .selected-text {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .dropdown-arrow {
        margin-left: 8px;
        font-size: 10px;
        color: var(--body-text-color-secondary);
    }
}

.multi-select-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 4px;
    background: white;
    border: 1px solid var(--gray-200);
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1000;
}

.checkbox-item {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    cursor: pointer;
    user-select: none;

    &:hover {
        background: var(--gray-100);
    }

    input[type="checkbox"] {
        margin-right: 8px;
        cursor: pointer;
    }

    span {
        font-size: 14px;
        color: var(--body-text-color);
    }
}

.dropdown-actions {
    display: flex;
    gap: 8px;
    padding: 8px 12px;
    border-top: 1px solid var(--gray-200);
    background: var(--gray-50);

    button {
        flex: 1;
        padding: 6px 12px;
        border: none;
        border-radius: 4px;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;

        &.btn-clear {
            background: var(--gray-100);
            color: var(--body-text-color);

            &:hover {
                background: var(--gray-200);
            }
        }

        &.btn-apply {
            background: var(--primary-color, #1976d2);
            color: white;

            &:hover {
                background: var(--primary-color-dark, #1565c0);
            }
        }
    }
}

.status-select {
    cursor: pointer;
}

.help-tip-container {
    margin-left: auto;
    align-self: flex-start;
}

.main-display-area {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.tasks-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow: auto;
    padding: 16px;
    gap: 16px;
}

.task-section {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.task-section-header {
    padding: 12px 16px;
    background: var(--gray-100);
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s;
    
    &:hover {
        background: var(--gray-200);
    }
}

.section-title-container {
    display: flex;
    align-items: center;
    gap: 12px;
}

.collapse-icon {
    font-size: 0.8rem;
    color: var(--body-text-color-secondary);
    transition: transform 0.2s;
}

.section-title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--body-text-color);
}

.tasks-list {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    gap: 16px;
    padding: 16px 0;
}

.empty-message {
    width: 100%;
    text-align: center;
    padding: 20px;
    color: var(--body-text-color-secondary);
    font-style: italic;
}

.empty-state {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;
    width: 100%;
    height: 100%;
}

.loading {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
}
</style>
